import { createSelector } from 'reselect';

import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import { CLOUD_STORAGE_METHODS } from '@iconik-io/shared-lib/src/utils/constants/storages';
import type { UploaderState } from '@iconik-io/uploader/src/state';

import { selectPermissions } from '../../components/permissions_manager/selectors';
import type { FilesAssetSubtitle } from '../../ducks/files/types';
import { selectAssetFormats } from '../../ducks/pages/asset/selectors';
import { selectUploads } from '../../ducks/uploader/selectors';
import {
  getUploadAssetId,
  getUploadedBytes,
  getUploadFileName,
  isProxyUpload,
} from '../../ducks/uploader/upload_state';
import type { AppState } from '../../store/types';
import type { PermissionValues } from '../../types/common';
import { selectUserRoles } from '../../utils/get_user_roles';
import { hasMediaFormat, hasNonMediaFormat } from '../../utils/media_type';
import type { FormatProgress } from './types';

const getAssetPermissions = (
  state: AppState,
  assetId: string | undefined,
): PermissionValues | undefined =>
  selectPermissions(state, OBJECT_TYPE_ASSETS, assetId);

const getAssetId = (state: AppState) => state.pages.asset.assetId;

export const getSubtitles = (state: AppState) =>
  state.filesReducer.assetSubtitles;

export const selectSubtitlesFormats = createSelector(
  selectAssetFormats,
  (assetFormats) => {
    const formats = assetFormats.filter(
      (format) => format.name === 'SUBTITLES',
    );
    return formats;
  },
);

export const selectNonMediaFormats = createSelector(
  selectAssetFormats,
  (assetFormats) => assetFormats.filter(hasNonMediaFormat),
);

export const selectMediaFormats = createSelector(
  selectAssetFormats,
  (assetFormats) => assetFormats.filter(hasMediaFormat),
);

export const selectSubtitles = createSelector(
  getSubtitles,
  (subtitles): { [formatId: string]: FilesAssetSubtitle } =>
    Object.fromEntries(
      subtitles.map((subtitle) => [subtitle.format_id, subtitle]),
    ),
);

export const selectResolvedPermissions = createSelector(
  selectUserRoles,
  getAssetPermissions,
  (userRoles, assetPermissions) => ({
    write: Boolean(userRoles.canWriteFormats && assetPermissions?.write),
    delete: Boolean(userRoles.canDeleteFormats && assetPermissions?.delete),
  }),
);

export const selectCanRequestOriginal = createSelector(
  selectUserRoles,
  selectAssetFormats,
  getAssetPermissions,
  (roles, formats, permissions) => {
    const hasPermissions = permissions?.write && roles.canWriteTransfers;
    const hasOriginal = formats.some((format) => format.name === 'ORIGINAL');

    const isOriginalStoredInCloud = formats.some(
      (format) =>
        format.name === 'ORIGINAL' &&
        format.storage_methods?.some((method) =>
          CLOUD_STORAGE_METHODS.includes(method),
        ),
    );

    return hasPermissions && hasOriginal && !isOriginalStoredInCloud;
  },
);

const getProgress = (upload: UploaderState): number => {
  const total = upload.fileInfo.size;
  return total === 0 ? 1 : Math.round((getUploadedBytes(upload) * 100) / total);
};

export const selectUploading = createSelector(selectUploads, (uploads) => {
  const formatProgress: FormatProgress = {};
  uploads.forEach((upload) => {
    const formatId =
      upload.uploadKind.kind === 'file'
        ? upload.uploadKind.formatId
        : undefined;
    if (formatId) {
      formatProgress[formatId] = {
        progress: getProgress(upload),
        name: getUploadFileName(upload),
        status: upload.status,
      };
    }
  });
  return formatProgress;
});

export const selectUploadingProxy = createSelector(
  selectUploads,
  getAssetId,
  (uploads, assetId) => {
    const proxy = uploads.findLast(
      (upload) => getUploadAssetId(upload) === assetId && isProxyUpload(upload),
    );

    if (proxy) {
      return {
        progress: getProgress(proxy),
        name: getUploadFileName(proxy),
        status: proxy.status,
      };
    }
    return undefined;
  },
);
